import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  abgleichText,
  istGast,
  istTeam,
  PERSON_ROLE_LABELS,
  type PersonRole,
} from '@netcon-work/shared'
import { LogOut, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { abmelden } from '../lib/auth'
import { ErinnerungsWahl, VorwarnWahl } from './ErinnerungsWahl'
import { MicrosoftVerbindung } from './MicrosoftVerbindung'
import { NotizbuchNavEintrag } from '../notizbuch'
import { cn } from '../lib/cn'
import { useTelefonLayout } from '../lib/medien'
import { useNavSchmal } from '../lib/navbreite'
import { useAktiveWelt } from '../lib/welt'
import { Tipp } from './Tipp'
import { farbklassen } from './WorkspaceMarke'
import { GRUPPEN_TITEL, navigationFuer, type NavEintrag } from '../lib/navigation'
import { abgleichQuery } from '../lib/queries'
import type { Verbindungsstatus } from '../lib/realtime'
import { Marke } from './Marke'
import { useSchublade } from './Schublade'
import { WorkspaceUmschalter } from './WorkspaceUmschalter'

/** Zähler an einzelnen Punkten – kommt später aus echten Daten. */
export interface NavZaehler {
  [id: string]: { anzahl?: number | undefined; punkt?: boolean | undefined } | undefined
}

export interface AngezeigtePerson {
  displayName: string
  role: PersonRole
}

export function Sidebar({
  zaehler,
  status,
  person,
}: {
  zaehler: NavZaehler
  status: Verbindungsstatus
  person: AngezeigtePerson
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { offen, schliessen } = useSchublade()
  const eintraege = navigationFuer(person.role)
  const telefon = useTelefonLayout()
  const [schmalGewuenscht, setSchmal] = useNavSchmal()
  /** Schmal nur am großen Bildschirm – in der Schublade ist Platz genug. */
  const schmal = schmalGewuenscht && !telefon

  let letzteGruppe: NavEintrag['gruppe'] | null = null

  return (
    /**
     * **Ab `lg` steht sie, darunter fährt sie ein** ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)).
     *
     * Eine Leiste, zwei Verhaltensweisen – und nicht zwei Komponenten: die Navigation ist
     * dieselbe, und eine zweite Fassung wäre eine zweite Stelle, an der ein neuer Punkt
     * vergessen werden kann.
     *
     * Unterhalb der Grenze `fixed` mit `-translate-x-full`, oberhalb `lg:sticky` und
     * `lg:translate-x-0`. Beide Anweisungen stehen gleichzeitig da; die mit `lg:` gewinnt am
     * großen Bildschirm, weil Tailwind Varianten hinter die Grundklassen sortiert. `overflow-y-auto`
     * kommt neu dazu: auf einem niedrigen Telefon im Querformat passt die Navigation sonst
     * nicht auf den Schirm, und die Abmelden-Zeile am Fuß wäre unerreichbar.
     */
    <aside
      id="hauptnavigation"
      aria-label="Hauptnavigation"
      className={cn(
        'fixed bottom-0 unter-leiste left-0 z-50 flex w-[248px] flex-col gap-0.5 overflow-y-auto border-r border-line bg-surface pt-5 pb-4 transition-transform duration-200',
        schmal ? 'px-2' : 'px-3.5',
        offen ? 'translate-x-0' : '-translate-x-full',
        'lg:sticky lg:unter-leiste lg:z-auto lg:hoehe-ohne-leiste lg:w-auto lg:translate-x-0 lg:transition-none',
      )}
    >
      {schmal ? (
        <div className="flex flex-col items-center gap-2 pb-3">
          <Marke className="size-8 rounded-[9px]" />
          <Tipp text="Menü aufklappen" an>
            <button
              type="button"
              onClick={() => setSchmal(false)}
              aria-label="Menü aufklappen"
              className="cursor-pointer rounded-lg p-1.5 text-muted hover:bg-[#f3f2ef] hover:text-ink"
            >
              <PanelLeftOpen className="size-[16px]" />
            </button>
          </Tipp>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-2 pb-[18px]">
          <Marke className="size-8 rounded-[9px]" />
          <div className="text-[15px] leading-tight font-bold tracking-[-0.2px]">
            NetCon Work
            <small className="block text-[11px] font-normal tracking-normal text-muted">
              Aufgabensteuerung
            </small>
          </div>
          {/* Nur in der Schublade: am Rechner gibt es nichts zu schließen. */}
          <button
            type="button"
            onClick={schliessen}
            aria-label="Menü schließen"
            className="-mr-1 ml-auto cursor-pointer rounded-lg p-2 text-muted hover:bg-[#f3f2ef] hover:text-ink lg:hidden"
          >
            <X className="size-[18px]" />
          </button>
          {/* Am großen Bildschirm: auf Symbole einklappen. */}
          <button
            type="button"
            onClick={() => setSchmal(true)}
            aria-label="Menü einklappen"
            title="Menü einklappen – nur Symbole"
            className="-mr-1 ml-auto hidden cursor-pointer rounded-lg p-1.5 text-muted hover:bg-[#f3f2ef] hover:text-ink lg:block"
          >
            <PanelLeftClose className="size-[16px]" />
          </button>
        </div>
      )}

      <nav className="flex flex-col gap-0.5">
        {eintraege.map((eintrag, i) => {
          const titel = letzteGruppe !== eintrag.gruppe ? GRUPPEN_TITEL[eintrag.gruppe] : null
          letzteGruppe = eintrag.gruppe
          const aktiv = pathname === eintrag.path
          /**
           * **Der Umschalter steht vor dem ersten gebundenen Punkt**
           * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
           *
           * Nicht an einer festen Stelle, sondern an der **Grenze**: „Mein Tag" steht darüber
           * und geht über alle Arbeitswelten, alles darunter gehört zu einer. Wird die
           * Reihenfolge der Navigation einmal geändert, wandert er mit – eine feste Position
           * wäre eine zweite Wahrheit über den Aufbau der Leiste.
           *
           * Für Rollen ohne gebundene Punkte – den Gast – erscheint er gar nicht, weil die
           * Bedingung nie zutrifft. Das ist dieselbe Antwort, die der Umschalter ohnehin
           * gäbe: er hat für einen Gast nichts zu zeigen.
           */
          const ersterGebundener =
            eintrag.workspaceGebunden && !eintraege.slice(0, i).some((e) => e.workspaceGebunden)
          const z = zaehler[eintrag.id]
          const Icon = eintrag.icon

          return (
            <div key={eintrag.id}>
              {ersterGebundener &&
                (schmal ? (
                  <WeltKnopf aufklappen={() => setSchmal(false)} />
                ) : (
                  <WorkspaceUmschalter />
                ))}
              {titel && schmal && <div className="mx-2 my-2 border-t border-line" />}
              {titel && !schmal && (
                <div className="mx-3 mt-4 mb-1.5 text-[10.5px] font-semibold tracking-[0.9px] text-muted uppercase">
                  {titel}
                </div>
              )}
              {schmal ? (
                <Tipp
                  text={z?.anzahl !== undefined ? `${eintrag.label} · ${z.anzahl}` : eintrag.label}
                  an
                >
                  <Link
                    to={eintrag.path}
                    aria-label={eintrag.label}
                    className={cn(
                      'relative flex h-10 w-full items-center justify-center rounded-[10px] transition-colors',
                      aktiv ? 'bg-accent-soft text-accent-deep' : 'text-ink2 hover:bg-[#f3f2ef]',
                    )}
                  >
                    <Icon className={cn('size-[18px]', aktiv ? 'opacity-100' : 'opacity-75')} />
                    {z?.anzahl !== undefined && z.anzahl > 0 && (
                      <span className="tabular absolute top-0.5 right-0.5 min-w-[16px] rounded-full bg-[#e6e5e0] px-1 text-center text-[9.5px] leading-[15px] font-bold text-ink2">
                        {z.anzahl > 99 ? '99+' : z.anzahl}
                      </span>
                    )}
                    {z?.punkt && (
                      <i className="absolute top-1.5 left-2 size-[7px] rounded-full bg-pool-sofort" />
                    )}
                  </Link>
                </Tipp>
              ) : (
                <Link
                  to={eintrag.path}
                  className={cn(
                    'flex w-full items-center gap-[11px] rounded-[10px] px-3 py-[9px] text-left text-[13.5px] font-medium transition-colors',
                    aktiv
                      ? 'bg-accent-soft font-semibold text-accent-deep'
                      : 'text-ink2 hover:bg-[#f3f2ef]',
                  )}
                >
                  <Icon
                    className={cn('size-[17px] shrink-0', aktiv ? 'opacity-100' : 'opacity-75')}
                  />
                  {eintrag.label}
                  {z?.punkt && <i className="ml-auto size-[7px] rounded-full bg-pool-sofort" />}
                  {z?.anzahl !== undefined && (
                    <span
                      className={cn(
                        'tabular rounded-full px-2 py-px text-[11px] font-semibold',
                        z.punkt ? 'ml-1.5' : 'ml-auto',
                        aktiv ? 'bg-[#d6e6fa] text-accent-deep' : 'bg-[#f0efec] text-ink2',
                      )}
                    >
                      {z.anzahl}
                    </span>
                  )}
                </Link>
              )}
              {/* Das Notizbuch bringt seinen Punkt selbst mit (E-419) – zeigt sich nur mit Häkchen. */}
              {eintrag.id === 'alle' && <NotizbuchNavEintrag schmal={schmal} />}
            </div>
          )
        })}
      </nav>

      {schmal ? (
        <div className="mt-auto flex flex-col items-center gap-2 border-t border-line pt-3">
          <Tipp text={`${person.displayName} · ${PERSON_ROLE_LABELS[person.role]}`} an>
            <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-accent to-src-work text-xs font-bold text-white">
              {initialen(person.displayName)}
            </div>
          </Tipp>
          <AbmeldeKnopf />
          <i
            className={cn(
              'size-[7px] rounded-full',
              status === 'verbunden'
                ? 'bg-good'
                : status === 'verbindet'
                  ? 'bg-warn'
                  : 'bg-pool-sofort',
            )}
            title={
              status === 'verbunden'
                ? 'Live verbunden'
                : status === 'verbindet'
                  ? 'Verbindet …'
                  : 'Getrennt'
            }
          />
        </div>
      ) : (
        <div className="mt-auto border-t border-line pt-3">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-src-work text-xs font-bold text-white">
              {initialen(person.displayName)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold">{person.displayName}</div>
              <div className="text-[11px] text-muted">{PERSON_ROLE_LABELS[person.role]}</div>
            </div>
            <AbmeldeKnopf />
          </div>
          {/* Eine persönliche Einstellung gehört zur Person, nicht in die Administration –
            der Mit-Owner sieht „Einstellungen" nie und ist der typische Abnehmer (Z-09). */}
          {!istGast(person.role) && (
            <>
              <ErinnerungsWahl />
              {/* Und dieselbe Überlegung für den Wecker vor einem Termin (Z-13). */}
              <VorwarnWahl />
              {/* Und für das eigene Microsoft-Konto – es gehört der Person, nicht der Administration (Z-17).
                Nur Team: die Aufgaben landen in „Mein Tag", und das hat der Fachbereich nicht. */}
              {istTeam(person.role) && <MicrosoftVerbindung />}
            </>
          )}
          <VerbindungsAnzeige status={status} />
        </div>
      )}
    </aside>
  )
}

function initialen(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0]?.toUpperCase() ?? '')
    .join('')
}

function AbmeldeKnopf() {
  const queryClient = useQueryClient()
  return (
    <button
      type="button"
      title="Abmelden"
      aria-label="Abmelden"
      onClick={() => {
        void abmelden().then(() => queryClient.invalidateQueries())
      }}
      className="ml-auto shrink-0 cursor-pointer rounded-lg p-1.5 text-muted hover:bg-[#f3f2ef] hover:text-ink"
    >
      <LogOut className="size-4" />
    </button>
  )
}

/**
 * **Wie alt ist, was ich sehe?** ([E-312](../../../../docs/ENTSCHEIDUNGEN.md#e-312), 02.09.2026)
 *
 * Aus Ticket NCW-6X848M: *„Statt «Live verbunden» unten links die Zeit bis zum Sync anzeigen."*
 *
 * „Live verbunden" war eine wahre Auskunft über die falsche Frage: sie sagte, ob der Browser mit
 * dem Server spricht. Er kann tadellos sprechen, während der GitLab-Abgleich seit einer Stunde
 * hängt – und der Bestand lebt laut Leitplanke 2 im Client, ist also genau so alt wie sein
 * letzter Abgleich.
 *
 * ## Die Verbindung verschwindet trotzdem nicht
 *
 * Sie beantwortet eine **zweite** Frage – *bekomme ich Änderungen überhaupt mit?* – und im
 * Störfall die dringendere. Deshalb: getrennt oder verbindend schlägt alles und steht allein da;
 * **nur wenn die Verbindung steht**, ist der Abgleich die interessantere Zahl.
 *
 * ## Warum die Zeit im Sekundentakt weiterläuft
 *
 * Ohne eigenen Takt stünde „vor 2 Min" auch noch nach zwanzig – die Abfrage kommt alle 60
 * Sekunden, aber gerechnet wird gegen `jetzt`. Ein `useState` mit `setInterval` ist hier das
 * billigste Mittel: eine Zahl, eine Zeile, kein Rundlauf.
 */
function VerbindungsAnzeige({ status }: { status: Verbindungsstatus }) {
  const stil: Record<Verbindungsstatus, { text: string; farbe: string; punkt: string }> = {
    verbunden: { text: 'Live verbunden', farbe: 'text-good-text', punkt: 'bg-good' },
    verbindet: { text: 'Verbindet …', farbe: 'text-warn', punkt: 'bg-warn animate-pulse' },
    getrennt: {
      text: 'Getrennt – Daten veraltet',
      farbe: 'text-pool-sofort',
      punkt: 'bg-pool-sofort',
    },
  }
  const s = stil[status]

  const { data: abgleich } = useQuery(abgleichQuery)
  const [jetzt, setJetzt] = useState(() => new Date())
  useEffect(() => {
    const takt = window.setInterval(() => setJetzt(new Date()), 30_000)
    return () => window.clearInterval(takt)
  }, [])

  /** Steht die Verbindung nicht, ist das die Nachricht – der Abgleich wäre dann Beiwerk. */
  if (status !== 'verbunden' || !abgleich) {
    return (
      <div className={cn('mt-2 flex items-center gap-2 px-2 text-[11px] font-medium', s.farbe)}>
        <i className={cn('size-[7px] rounded-full', s.punkt)} />
        {s.text}
      </div>
    )
  }

  const stand = {
    ...abgleich,
    zuletzt: abgleich.zuletzt ? new Date(abgleich.zuletzt) : null,
    naechster: abgleich.naechster ? new Date(abgleich.naechster) : null,
  }

  return (
    <div
      className={cn(
        'mt-2 flex items-center gap-2 px-2 text-[11px] font-medium',
        stand.haengt ? 'text-pool-sofort' : 'text-muted',
      )}
      title={`Älteste Quelle: ${stand.quelle}. Die Verbindung zum Server steht.`}
    >
      <i className={cn('size-[7px] rounded-full', stand.haengt ? 'bg-pool-sofort' : 'bg-good')} />
      {abgleichText(stand, jetzt)}
    </div>
  )
}

/**
 * **Die Arbeitswelt in der schmalen Leiste**: ihr Zeichen in ihrer Farbe. Wechseln geht im
 * breiten Menü – ein Klick klappt es auf.
 */
function WeltKnopf({ aufklappen }: { aufklappen: () => void }) {
  const { welt } = useAktiveWelt()
  if (!welt) return null
  const f = farbklassen(welt.farbe)
  return (
    <Tipp text={`Arbeitswelt: ${welt.label} – klicken zum Wechseln`} an>
      <button
        type="button"
        onClick={aufklappen}
        aria-label={`Arbeitswelt ${welt.label} wechseln`}
        className={cn(
          'mx-auto my-2 flex size-10 cursor-pointer items-center justify-center rounded-[10px] text-[18px]',
          f.flaeche,
          f.text,
        )}
      >
        {welt.icon || welt.label.slice(0, 1)}
      </button>
    </Tipp>
  )
}
